import { EVENT_SITES } from '@crossbeam/itly';

import { orderBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

import { crossbeamApi } from '@/api';
import useIteratively from '@/composables/useIteratively';
import useOfflinePartner from '@/composables/useOfflinePartner';
import usePopulationShares from '@/composables/usePopulationShares';
import { validateAndConvertOperandDataTypes } from '@/constants/populations';
import {
  STANDARD_POPULATION_TYPES,
  emptyPopulation,
  getPopulationByStandardType,
} from '@/constants/standard_populations';
import { captureException } from '@/errors';
import {
  useBillingStore,
  useDataRequestsStore,
  useDataSharesStore,
  useFlashesStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import { isOpenDataPopulation } from '@/types/populations';

export default function usePopulations(
  partnerOrgId = null,
  offlinePartnerUuid = null,
  feedId = null,
) {
  const populationsStore = usePopulationsStore();
  const sourcesStore = useSourcesStore();
  const dataSharesStore = useDataSharesStore();
  const billingStore = useBillingStore();
  const dataRequestsStore = useDataRequestsStore();
  const flashesStore = useFlashesStore();
  const route = useRoute();

  const filteredPopulations = computed(() => {
    if (!feedId) return populations.value;

    const sourceIds = sourcesStore.getSourcesByFeedId(feedId).map((s) => s.id);
    return populations.value.filter((pop) => sourceIds.includes(pop.source_id));
  });

  /* Offline partner state */
  const { offlineSources, refreshOfflineData } = useOfflinePartner();

  const { iteratively } = useIteratively();
  const { addMetadataToPopulation } = usePopulationShares();

  const {
    populations,
    populationStats,
    customPopulations,
    inactivePopulations,
  } = storeToRefs(populationsStore);
  const { isFreeTier, customPopulationLimit } = storeToRefs(billingStore);

  onMounted(async () => {
    await Promise.all([
      populationsStore.readySync,
      sourcesStore.readySync,
      dataSharesStore.readySync,
      dataRequestsStore.readySync,
    ]);

    if (offlinePartnerUuid) await refreshOfflineData(offlinePartnerUuid);
  });

  const allPopulations = computed(() => [
    customersPopulation.value,
    openOppsPopulation.value,
    prospectsPopulation.value,
  ]);

  /* Standard populations */
  const openOppsPopulation = computed(() =>
    buildPopulation('open_opportunities'),
  );
  const customersPopulation = computed(() => buildPopulation('customers'));
  const prospectsPopulation = computed(() => buildPopulation('prospects'));
  const standardPopulations = computed(() =>
    allPopulations.value.filter((population) => !population.isEmpty),
  );

  /* Custom populations */
  const customPopsWithStatsAndSharing = computed(() => {
    if (filteredPopulations.value) {
      const base = [...customPopulations.value]; // Create a copy to avoid mutation
      if (isFreeTier.value) base.push(...inactiveCustomPops.value);
      return orderBy(
        base.map((pop) => addMetadataToPopulation(pop, partnerOrgId)),
        [(pop) => pop.name.toLowerCase()],
      );
    }
    return [];
  });

  const inactiveCustomPops = computed(() => {
    return inactivePopulations.value.filter(
      (pop) => !STANDARD_POPULATION_TYPES.includes(pop.standard_type),
    );
  });

  const userCreatedCustomPopulations = computed(() =>
    customPopulations.value.filter((pop) => !isOpenDataPopulation(pop)),
  );

  const atOrAboveCap = computed(
    () =>
      userCreatedCustomPopulations.value.length >= customPopulationLimit.value,
  );
  const isEarlyAdopter = computed(
    () =>
      userCreatedCustomPopulations.value.length > customPopulationLimit.value,
  );

  /* CRUD Operations for Populations */
  async function previewPopulation(population, sourceFilterExpressions) {
    const payload = makePreviewPayload(population, sourceFilterExpressions);
    const { data, error } = await crossbeamApi.POST(
      '/v0.3/population-preview',
      {
        body: payload,
        ...(offlinePartnerUuid && {
          params: { query: { offline_partner_org_uuid: offlinePartnerUuid } },
        }),
      },
    );
    if (error) {
      captureException(error);
      flashesStore.addUnhandledError(error);
      return INITIAL_DATA;
    }
    return data;
  }

  async function previewPopulationCount(population, sourceFilterExpressions) {
    const payload = makePreviewPayload(population, sourceFilterExpressions);
    const { data, error, response } = await crossbeamApi.POST(
      '/v0.3/population-preview-count',
      {
        body: payload,
        ...(offlinePartnerUuid && {
          params: { query: { offline_partner_org_uuid: offlinePartnerUuid } },
        }),
      },
    );
    if (response.status === 504) {
      captureException(error);
      return { count: 0, timeout: true, error: false };
    }
    if (error) {
      flashesStore.addUnhandledError(error);
      captureException(error);
      return { count: 0, timeout: false, error: true };
    }
    return { count: data.count, timeout: false, error: false };
  }

  async function savePopulation(population, payload) {
    const isPatch = !route.path.includes('create');
    const axiosOperation = isPatch ? crossbeamApi.PATCH : crossbeamApi.POST;
    let args = [];

    if (offlinePartnerUuid)
      args = isPatch
        ? [
            '/v0.3/populations/{id}',
            {
              params: {
                path: { id: population.id },
                query: { offline_partner_org_uuid: offlinePartnerUuid },
              },
            },
          ]
        : [
            '/v0.3/populations',
            {
              params: {
                query: { offline_partner_org_uuid: offlinePartnerUuid },
              },
            },
          ];
    else
      args = isPatch
        ? [
            '/v0.3/populations/{id}',
            {
              params: {
                path: { id: population.id },
              },
            },
          ]
        : ['/v0.3/populations', {}];

    const [url, options] = args;
    const response = await axiosOperation(url, {
      body: payload,
      ...options,
    });
    const parseResponse = (x) =>
      isPatch ? { ...x, meta: population.meta } : { ...x, meta: [] };
    const newPopulation = parseResponse(response.data);

    if (!isPatch) {
      const iterativelyPayload = {
        population_name: newPopulation.name,
        population_id: newPopulation.id.toString(),
        event_site: EVENT_SITES.POPULATION_DETAIL,
      };
      iteratively.userCreatedPopulation(iterativelyPayload);
    }

    const scrubbedPopName = scrubString(population.name);
    const message = `${scrubbedPopName} has been ${isPatch ? 'updated' : 'created'}`;
    flashesStore.addSuccessFlash({
      message,
      description: 'It will take some time for these changes to be reflected.',
    });
    populationsStore.upsertPopulation(newPopulation);
    await populationsStore.refreshPopulationsStore();
    await dataSharesStore.refreshDataSharesStore();
    return newPopulation;
  }

  async function deletePopulation(population) {
    const name = population.name;
    const id = population.id;
    const { error } = await crossbeamApi.DELETE('/v0.2/populations/{id}', {
      params: {
        path: { id },
      },
    });
    if (error) throw error;
    populationsStore.removePopulation(id);
    dataSharesStore.removeDataShareByPopulationId(id);
    dataRequestsStore.removeDataRequestByPopulationId(id);
    flashesStore.addSuccessFlash({ message: `${name} Deleted` });
  }

  /* Helpers 🤝 */

  function buildPopulation(type) {
    const population = getPopulationByStandardType(
      type,
      filteredPopulations.value,
    );
    if (!population) return emptyPopulation(type);
    return addMetadataToPopulation(population, partnerOrgId);
  }

  function makePopulationPayload(
    population,
    populationName,
    filterGroupList,
    standardType = null,
  ) {
    const isPatch = route.path.includes('edit');
    const payload = {
      name: populationName,
      description: population.description || null,
      standard_type: standardType,
      source_filter_expressions: formatFiltersForPayload(
        population.name,
        filterGroupList,
      ),
    };

    if (!isPatch) payload.source_id = population.source_id;

    return payload;
  }

  const scrubString = (string) => {
    const lt = /</g;
    const gt = />/g;
    const sq = /'/g;
    const dq = /"/g;
    return string
      .replace(lt, '&lt;')
      .replace(gt, '&gt;')
      .replace(sq, '&#39;')
      .replace(dq, '&#34;');
  };

  /* Saving + Editing Population */
  function formatFiltersForPayload(populationName, groupList = []) {
    const filters = {};

    const allSources = offlinePartnerUuid
      ? offlineSources.value
      : sourcesStore.sources;

    groupList.forEach((group) => {
      if (!group.tableName) return;

      const completedFilterParts = group.filter_parts.filter((item) => {
        return item && item.source_field_id && item.operator;
      });
      const convertedFilterParts = completedFilterParts.map((filterPart) => {
        const sourceField = allSources
          .find((s) =>
            s.fields?.find((f) => filterPart.source_field_id === f.id),
          )
          ?.fields.find((f) => filterPart.source_field_id === f.id);
        if (!sourceField) {
          captureException(
            new Error(
              `Unresolved source field ${filterPart.source_field_id} for population: ${populationName}`,
            ),
          );
          return filterPart;
        }

        const { convertedOperands, operandValidationErrors } =
          validateAndConvertOperandDataTypes(
            sourceField.data_type,
            filterPart.operand,
          );
        if (operandValidationErrors.length) {
          const error = new Error(
            `Operand Validation Errors: ${populationName}`,
          );
          captureException(error);
        }
        return {
          ...filterPart,
          operand: convertedOperands,
        };
      });
      filters[group.tableName] = {
        filter_expression: group.filter_expression,
        filter_parts: convertedFilterParts,
      };
    });
    return filters;
  }

  function makePreviewPayload(population, sourceFilterExpressions) {
    const payload = {
      base_schema: population.base_schema,
      base_table: population.base_table,
      source_filter_expressions: sourceFilterExpressions ?? {},
      source_id: population.source_id,
    };

    if (offlinePartnerUuid)
      payload.offline_partner_org_uuid = offlinePartnerUuid;

    return payload;
  }

  const INITIAL_DATA = {
    fields: [],
    rows: [],
  };

  function getRecordNumber(population) {
    // TODO: Change w/ offline partner data
    if (!population) return;
    return populationStats.value.find(
      (stat) => stat.population_id === population.id,
    )?.total_size;
  }

  return {
    INITIAL_DATA,
    populations: filteredPopulations,
    inactivePopulations,
    allPopulations,
    customersPopulation,
    standardPopulations,
    populationsReady: populationsStore.readySync,
    getRecordNumber,
    /* Custom Pops */
    customPopsWithStatsAndSharing,
    customPopulations,
    atOrAboveCap,
    isEarlyAdopter,
    /* CRUD */
    deletePopulation,
    savePopulation,
    previewPopulation,
    previewPopulationCount,
    makePopulationPayload,
  };
}
